import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  LockOutlined,
  MenuOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  ExportButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import { Popover, Progress, Space, Spin, Tooltip } from 'antd';
import { ScopeTabs } from 'components/Custom/tabs';
import ChromeExtensions2Png from 'assets/images/icons/chrome-extensions2.png';
import { ReactComponent as IconRefresh } from 'assets/images/icons/iconRefresh.svg';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import * as React from 'react';
import { AccountType, UpgradeMessageSource } from 'store/api/enums';
import {
  DataLogActions,
  DataLogStatus,
  DrawerType,
  MenuSize,
  TaxReportCountry,
  VatTaxReportType,
} from 'store/app/enums';
import { User } from 'store/auth/models';
import {
  ActivityLog,
  ActivityRoleUpdateRequest,
  VatReportData,
  VatReturnLog,
  VatRunData,
} from 'store/vat/models';
import styled from 'styled-components';
import { ActivityTab } from './activity-tab';
import { ActivityLogs } from './activity-logs';
import { CompareReturnModal } from './CompareReturnModal';
import { ErrorWrapper, renderErrorMessage } from './render-error-messages';
import { renderCyprusRecap } from './tables/render-cyprus-recap';
import { renderMaltaRecap } from './tables/render-malta-recap';
import { TransactionTable } from './tables/transactions';
import { VatReturnForms } from './vat-return-forms';

import Logo from 'assets/images/generateTAX-logo.png';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { Entity } from 'store/organisation/models';
import { DataJournal, ExcelFileResponse } from 'store/vat/models/vatReportData';
import { AvatarGroupRenderer } from 'utils/avatar-group-renderer';
import { AvatarSizes } from 'utils/render-avatar';
import { StatusGetter } from 'utils/status-getter';
import { checkDisabled } from './check-disabled';
import { getActionMenuItems } from './get-action-menu-items';
import { getNextButton } from './get-next-button';
import { hasReportWorkflowContext } from './report-workflow';

import ChromeExtensionPic from 'assets/images/chrome-extension-pic.svg';
import GenTaxImage from 'assets/images/gen-tax-img-cropped.svg';
import { ReactComponent as IconClose } from 'assets/images/icons/iconClose.svg';
import PendingReview from 'assets/images/pending-review.svg';
import { ConnectEntity } from 'components/ConnectEntity';
import { EntityNotConnectedPanel } from 'components/OrganisationDetails/EntityNotConnectedPanel';
import { StyledCard } from 'components/Custom/card';
import {
  BlackSpan,
  BoldNameSpan,
  CardDescSpan,
  CardTitleLargeSpan,
} from 'components/Custom/spans';
import SseNotifications from 'components/ServerSentEvents';
import { MessageStates } from 'containers/MessageBox';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCountryLabel } from 'hooks/useCountryLabel';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import {
  formatDisplayDate,
  formatDisplayDateRange,
} from 'i18n/calendarLabels';
import { isResponsivePanelWideLayout } from 'utils/responsive-panel-breakpoints';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthUserLicence } from 'store/auth/selectors';
import {
  turnDrawerOn,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import { AdvancedUserRightsCodes, WindowType } from 'store/app/enums';
import { MessageData } from 'store/app/types';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import { SubscriptionType } from 'store/billing/models/subscriptionType';
import { MappingTaxRate } from 'store/returns/models/mappingTaxRate';
import { Section } from 'store/returns/models/returnData';
import {
  exportToExcelRequest,
  loadReportRequest,
  updateSummarizedDataSize,
} from 'store/vat/actions';
import { ExceptionsActionType } from 'store/vat/models/exceptionWrapper';
import { BoxValue } from 'store/vat/types';
import { handleExtensionCalls } from 'utils/handleExtensionCalls';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { handlePwaReturnBack as navigatePwaVatReportBack } from 'utils/pwa-vat-report-back';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { createPortal } from 'react-dom';
import routes from 'routes';
import { PwaIcon } from 'pwa/components/Icon';
import { PwaChip, PwaSegmented, PwaUserPic } from 'pwa/components/ui';
import {
  isSmallOrMediumPlanLocked,
  isSmallPlanLocked,
} from 'utils/subscriptionPlanGate';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
} from 'utils/preferences';
import { MappingOverview } from '../Mapping';
import { RenderExceptions } from './tables/render-exceptions';

//Testing
const VatReportWrapper = styled.div`
  .top-wrapper {
    border-bottom: 2px solid var(--color-border, #f0f0f0);
    background: var(--color-surface-base, #ffffff);
    display: flex;
    justify-content: space-between;
    height: 65px;
    position: fixed;
    z-index: 100;
    min-width: 800px;
  }

  .content-wrapper {
    font-family: Inter;
    background: var(--color-surface, #f3f7f8);
    display: flex;
    justify-content: space-between;
    gap: 40px;
    //min-height: 100vh;
    padding: 40px;
  }

  .form-wrapper {
    //padding-left: 20px;
    //background: rgb(255, 255, 255);
    display: flex;
    flex-direction: column;
  }

  .ant-tabs-nav-wrap {
    padding: 0px 0px 0px 40px;
  }

  .vat-report-main {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    height: auto;
  }

  .menu-wrapper {
    background: var(--color-surface, #f3f7f8);
    width: 360px;
    flex: 0 0 360px;
    min-width: 360px;
    z-index: 102;
    box-sizing: border-box;
  }

  .menu-top-wrapper {
    //border-bottom: 2px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: end;
    padding-right: 60px;
    gap: 20px;
    position: absolute;
    z-index: 103;
    right: 0px;
    top: 80px;
    float: right;
  }

  .menu-burger-button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100px;
  }
  .menu-burger-icon {
    cursor: pointer;
    width: 30px;
    height: 30px;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--color-text-base, #042a2a);
  }

  .burger-on {
    box-shadow: 2px 2px 2px 0px #adc2b94d inset;
    border: 1px solid var(--color-border, #dbeae3);
    background: var(--color-surface, #f3f7f8);
    &:hover,
    &:active {
      border: 1px solid var(--color-primary, #41aaaf);
    }
  }

  .burger-off {
    border: 1px solid transparent;
    background: none;
    &:hover,
    &:active {
      border: 1px solid var(--color-border, #dbeae3);
      background: var(--color-surface, #f3f7f8);
    }
  }

  .menu-burger-text {
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    text-align: left;
    color: var(--color-text-base, #042a2a);
  }

  .menu-entity-name {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: var(--color-text-base, #042a2a);
  }

  .menu-entity-details {
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    color: var(--color-text-base, #042a2a);
  }

  .tabs-wrapper {
    position: fixed;
    width: 100%;
    align-self: flex-start;
    z-index: 101;
    background: var(--color-surface-base, #ffffff);

    .ant-tabs-nav {
      background: var(--color-surface-base, #ffffff) !important;
    }

    .ant-tabs-tab-btn {
      color: var(--color-text-muted, #5a6a6a) !important;
    }

    .ant-tabs-tab:hover .ant-tabs-tab-btn,
    .ant-tabs-tab-active .ant-tabs-tab-btn {
      color: var(--color-text-dark, #042a2a) !important;
    }

    /* Submissions tabs — keep ink bar + nav divider on-brand during Spin refresh */
    .ant-tabs-ink-bar,
    .ant-tabs-ink-bar-animated {
      background: var(--color-primary, #41aaaf) !important;
      background-color: var(--color-primary, #41aaaf) !important;
    }

    .ant-tabs-top > .ant-tabs-nav::before,
    .ant-tabs-nav::before {
      border-bottom: 1px solid var(--color-border, #f0f0f0) !important;
    }

    .ant-spin-blur .ant-tabs-ink-bar,
    .ant-spin-blur .ant-tabs-ink-bar-animated {
      background: var(--color-primary, #41aaaf) !important;
      background-color: var(--color-primary, #41aaaf) !important;
    }
  }

  .ant-progress-text {
    color: var(--color-primary, #41aaaf) !important;
  }

  .csv-link-wrapper {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
  }
  .csv-link-wrapper:hover {
    text-decoration: none;
  }

  .ant-empty-description:after {
    content: ' found for this period' !important;
  }

  .ant-empty .ant-empty-description {
    color: var(--color-text-muted, #5a6a6a);
  }
`;

import { isOssReturnType } from 'utils/oss-mapping';
interface VatReportProps {
  loading: boolean;
  attachmentLoading: boolean;
  success: boolean;
  vatRunData: VatRunData;
  vatReportData?: VatReportData;
  generatedDate: string;
  reloadReportData: (
    data: VatRunData,
    isSaved: boolean,
    refresh: boolean
  ) => void;
  currentUser: User;
  loadingPercent: number;
  resetValues: boolean;
  resetFinished: () => void;
  pages: number;
  saveReturn: (vatRunData: VatRunData) => void;
  revertData: (vatRunData: VatRunData) => void;
  cancelReturn: (vatRunData: VatRunData) => void;
  setAwaitingApproval: (vatRunData: VatRunData) => void;
  setInternallyApproved: (vatRunData: VatRunData) => void;
  sendForClientApproval: (vatRunData: VatRunData) => void;
  setClientApproved: (vatRunData: VatRunData) => void;
  setNotApproved: () => void;
  cancelNotSaved: (vatRunData: VatRunData) => void;
  updateActivityRole: (
    activityRoleUpdateRequest: ActivityRoleUpdateRequest
  ) => void;
  activityLogs?: ActivityLog[];
  updateNote: (noteText: string, dataLogId: string) => void;
  cannotSave: boolean;
  menuSize: MenuSize;
  currentOrganisation?: Entity;
  mappingSections?: Section[];
  returnTypeId: string;
  excelFileData?: ExcelFileResponse;
  onReconnect: () => void;
  //getExcelFileForExport: () => void;
  loackWarningWindow: (vatRunData, actionType) => void;
  openAdjustmentPanal: (
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void
  ) => void;
  handlePreferences: (
    preferenceSection: PreferenceSections,
    action: PreferenceActions,
    preference: Preference
  ) => any;
  addExceptionAction: (
    exceptionActionType: ExceptionsActionType,
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void,
    selectedAdjustmentPeriod: string | null,
    selectedCaptureDate: any
  ) => void;
  noDataFound: boolean;
  subscriptionPlanTypes: SubscriptionType[];
  onDownloadXMLClick: () => void;
  onDownloadCSVClick: () => void;
  redirectPage: (url: string) => void;
  reportId: string;
  loadCustomersAndSuppliers?: (connectionId: string) => void;
  activateVatChecker?: (
    connectionId: string,
    orgName: string,
    uuid: string
  ) => void;
  currentSubscription?: CurrentSubscription;
  backendError: boolean;
  hasAdvancedUserRights: boolean;
  /** Malta TIFD report: VAT return and Mapping only. */
  returnAndMappingOnly?: boolean;
  /** Replaces the country VAT form. Used by the Malta TIFD report. */
  returnForm?: React.ReactNode;
  vatReturnLogs?: VatReturnLog[];
  onCompareReturn?: (dataLogId: string) => void;
  comparedReportData?: VatReportData | null;
  clearComparison?: () => void;
  journalType: string;
  setJournalType: (type: string) => void;
}

export const VatReport: React.FC<VatReportProps> = ({
  loading,
  attachmentLoading,
  success,
  vatRunData,
  vatReportData,
  generatedDate,
  reloadReportData,
  currentUser,
  loadingPercent,
  resetValues,
  resetFinished,
  pages,
  saveReturn,
  revertData,
  cancelReturn,
  setAwaitingApproval,
  setInternallyApproved,
  sendForClientApproval,
  setClientApproved,
  setNotApproved,
  cancelNotSaved,
  updateActivityRole,
  activityLogs,
  updateNote,
  cannotSave,
  menuSize,
  currentOrganisation,
  mappingSections,
  returnTypeId,
  onReconnect,
  excelFileData,
  loackWarningWindow,
  openAdjustmentPanal,
  handlePreferences,
  addExceptionAction,
  noDataFound,
  subscriptionPlanTypes,
  onDownloadXMLClick,
  onDownloadCSVClick,
  redirectPage,
  reportId,
  loadCustomersAndSuppliers,
  activateVatChecker,
  currentSubscription,
  backendError,
  hasAdvancedUserRights,
  returnAndMappingOnly = false,
  returnForm,
  vatReturnLogs,
  onCompareReturn,
  comparedReportData,
  clearComparison,
  journalType,
  setJournalType,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { localizeCountry } = useCountryLabel();
  const MESSAGE_FROM_EXTENSION = t('vat-report.service-not-available');
  const COPY_TO_EXTENSION = 'copyFromPage';
  const dispatch = useDispatch();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const userLicence = useSelector(getAuthUserLicence);

  const pwaReturnTitle = React.useMemo(() => {
    if (!vatRunData) return '';
    return vatRunData.taxReportTypeName || t('navigation.submission');
  }, [vatRunData, t]);

  const pwaReturnSubtitle = React.useMemo(() => {
    if (!vatRunData) return '';
    const country = localizeCountry(
      vatRunData.taxReportCountry,
      String(vatRunData.taxReportCountry || '')
    );
    const period =
      vatRunData.fromDate && vatRunData.toDate
        ? formatDisplayDateRange(
            vatRunData.fromDate,
            vatRunData.toDate,
            'D MMM YYYY',
            i18n.language
          )
        : vatRunData.generationPeriod || '';
    const currency = vatRunData.currency?.trim() || '';
    return [country, period, currency].filter(Boolean).join(' · ');
  }, [vatRunData, localizeCountry, i18n.language]);

  const handlePwaReturnBack = React.useCallback(() => {
    navigatePwaVatReportBack(currentOrganisation?.organisationId, navigate);
  }, [currentOrganisation?.organisationId, navigate]);

  const [showSideMenu, setShowSideMenu] = React.useState<boolean>(
    () => !pwaMobileNav
  );
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  React.useEffect(() => {
    if (pwaMobileNav) {
      setShowSideMenu(false);
      return;
    }
    if (windowWidth <= 0 || windowHeight <= 0) return;
    setShowSideMenu(isResponsivePanelWideLayout(windowWidth, windowHeight));
  }, [windowWidth, windowHeight, pwaMobileNav]);

  React.useEffect(() => {
    if (!(pwaMobileNav && showSideMenu)) {
      document.body.classList.remove('scope-pwa-vat-menu-open');
      return;
    }
    document.body.classList.add('scope-pwa-vat-menu-open');
    return () => {
      document.body.classList.remove('scope-pwa-vat-menu-open');
    };
  }, [pwaMobileNav, showSideMenu]);

  useOverlayBackHandler(
    Boolean(pwaMobileNav && showSideMenu),
    () => setShowSideMenu(false),
    'vat-report-menu',
    pwaMobileNav
  );
  const pwaMenuDragZoneRef = React.useRef<HTMLDivElement>(null);
  const closePwaSideMenu = React.useCallback(() => {
    setShowSideMenu(false);
  }, []);
  const pwaMenuDragClose = usePwaBottomSheetDragClose(
    pwaMobileNav && showSideMenu,
    closePwaSideMenu,
    pwaMenuDragZoneRef
  );
  const [sseMessage, setSseMessage] = React.useState<string | null>(null);
  const [summarizedStatusPending, setSummarizedStatusPending] =
    React.useState<boolean>(true);

  const isAdjustedReturn =
    !!vatReportData?.adjustedSubsections &&
    vatReportData?.adjustedSubsections.length > 0;

  const [activeKey, setActiveKey] = React.useState('1');

  React.useEffect(() => {
    if (!pwaMobileNav) return;
    document
      .querySelectorAll<HTMLElement>(
        '.scope-pwa-vat-report-tabs .ant-tabs-content-holder, .scope-pwa-vat-report-tabs .ant-tabs-tabpane'
      )
      .forEach((node) => {
        if (node.tabIndex >= 0) node.tabIndex = -1;
      });
  }, [pwaMobileNav, activeKey, loading]);
  const [filteredBox, setFilteredBox] = React.useState(null);
  const [enableDownloadFile, setEnableDownloadFile] =
    React.useState<boolean>(false);
  const [showCard, setShowCard] = React.useState(true);
  const [exceptionsSorted, setExceptionsSorted] =
    React.useState<DataJournal[]>();
  const [progress, setProgress] = React.useState(0);
  const [progressText, setProgressText] = React.useState(
    `${t('vat-report.connecting-to')} ${vatRunData.providerName}`
  );
  const [switchTotals, setSwitchTotals] = React.useState<boolean>(false);
  const [exporting, setExporting] = React.useState<boolean>(false);
  const isChrome =
    /Chrome/.test(navigator.userAgent) ||
    /Edge/.test(navigator.userAgent) ||
    /Edg/.test(navigator.userAgent);
  const [mappedForExtension, setMappedForExtension] =
    React.useState<boolean>(false);
  const [foundChromeExtension, setFoundChromeExtension] =
    React.useState<boolean>(true);
  const [showCompareModal, setShowCompareModal] =
    React.useState<boolean>(false);

  const lockClient = isSmallOrMediumPlanLocked(
    currentSubscription,
    userLicence,
    subscriptionPlanTypes
  );
  const lockExceptions = lockClient;
  const isCompareReturnLocked = isSmallPlanLocked(
    currentSubscription,
    userLicence,
    subscriptionPlanTypes
  );

  const handleCompareReturnUpgradeModal = () => {
    dispatch(
      turnModalOn({
        title: '',
        type: WindowType.WARNING,
        component: (
          <UpgradeMessage
            source={UpgradeMessageSource.COMPARE_RETURN}
            onClose={() => dispatch(turnModalOff())}
            userAccountType={currentUser?.accountType}
          />
        ),
      })
    );
  };

  React.useEffect(() => {
    let isMounted = true;

    const country = vatRunData.taxReportCountry;
    const taxReportType = vatRunData.taxType;
    const data = { country, taxReportType };
    if (!pwaMobileNav) {
      handleExtensionCalls('checkAvailability|' + JSON.stringify(data)).then(
        (response) => {
          if (isMounted) {
            if (response.success) {
              setMappedForExtension(true);
              setFoundChromeExtension(true);
            } else if (response.message === MESSAGE_FROM_EXTENSION) {
              setFoundChromeExtension(true);
              setMappedForExtension(false);
            } else {
              setFoundChromeExtension(false);
              setMappedForExtension(false);
            }
          }
        }
      );
    }

    if (isMounted) {
      setActiveKey('1');
      setProgress(1);
    }

    return () => {
      isMounted = false;
    };
  }, [pwaMobileNav]);

  React.useEffect(() => {
    if (!pwaMobileNav || returnAndMappingOnly) return;
    const mappingKey = pages === 4 ? '4' : pages === 3 ? '3' : null;
    if (mappingKey && activeKey === mappingKey) {
      setActiveKey('1');
    }
  }, [pwaMobileNav, pages, activeKey, returnAndMappingOnly]);

  React.useEffect(() => {
    loadingPercent === 0 && setProgress(1);
  }, [loadingPercent]);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    if (loadingPercent !== 100) {
      const providerLabel = t(
        `common.${vatRunData.providerName?.toLowerCase()}`,
        {
          defaultValue: vatRunData.providerName,
        }
      );
      const timerSecs =
        progress > 75 ? 1000 : progress > 50 ? 500 : progress > 75 ? 750 : 100;
      switch (progress) {
        case 0:
        case 80:
        case 60:
          if (isMounted) setProgressText(`Connecting to ${providerLabel}...`);
          break;
        case 15:
          if (isMounted)
            setProgressText(`Fetching data from ${providerLabel}...`);
          break;
        case 30:
        case 75:
          if (isMounted) setProgressText(t('vat-report.calculating'));
          break;
        case 45:
        case 90:
          if (isMounted) setProgressText(t('billing.processing'));
          break;
      }
      if (progress === 99) {
        if (isMounted) setProgress(83);
      } else {
        timeoutId = setTimeout(function () {
          if (isMounted) setProgress(progress + 1);
        }, timerSecs);
      }
    } else {
      if (isMounted) setProgress(100);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [progress]);

  React.useEffect(() => {
    const exceptSorted = vatReportData?.exceptions?.map((value, index) => ({
      ...value,
      key: index + 1,
    }));
    setExceptionsSorted(exceptSorted);
  }, [vatReportData?.exceptions]);

  React.useEffect(() => {
    setShowCard(
      !!(
        vatRunData?.status &&
        DataLogStatus[vatRunData.status] ===
          DataLogStatus.AWAITING_CLIENT_APPROVAL &&
        currentUser &&
        AccountType[currentUser?.accountType] === AccountType.CLIENT
      )
    );
  }, [vatRunData.status, currentUser]);

  React.useEffect(() => {
    setSummarizedStatusPending(true);
  }, [reportId]);

  // Load/cached reports never emit SUMMARIZING_* for the new reportId. Also
  // clear when the API (or a prior SSE) already populated summarizedSize.
  React.useEffect(() => {
    if ((vatReportData?.summarizedSize ?? 0) > 0) {
      setSummarizedStatusPending(false);
    }
  }, [vatReportData?.summarizedSize]);

  React.useEffect(() => {
    if (loading || !success || !vatReportData || !summarizedStatusPending) {
      return;
    }
    // Pages finished; stop the Summarised spinner even if SSE is late/missing.
    // SSE can still enable the tab later via summarizedSize.
    setSummarizedStatusPending(false);
  }, [loading, success, vatReportData, summarizedStatusPending]);

  React.useEffect(() => {
    if (sseMessage) {
      const receivedMessage = JSON.parse(sseMessage);

      const { id, status, message } = receivedMessage || {};
      if (id == reportId) {
        if (status === 'SUMMARIZING_COMPLETED') {
          const summarizedDataSize = Number(message);
          setSummarizedStatusPending(false);

          if (summarizedDataSize > 0) {
            dispatch(updateSummarizedDataSize(summarizedDataSize));
          }
        } else if (status === 'SUMMARIZING_FAILED') {
          setSummarizedStatusPending(false);
          const message: MessageData = {
            title: t('common.error'),
            description: t('transactions.summary-error'),
            type: MessageStates.ERROR,
            duration: 10,
          };
          dispatch(turnMessageOn(message));
        }
      }

      setSseMessage(null);
    }
  }, [sseMessage]);

  const jsonMappingString = vatRunData?.jsonMapping;
  const jsonMapping: MappingTaxRate[] = jsonMappingString
    ? JSON.parse(jsonMappingString).mapping
    : null;

  const mappingTab = () => {
    return (
      <>
        {currentOrganisation?.connectedProperly && (
          <MappingOverview
            mappedReturn={currentOrganisation?.returns.find(
              (r) => r.mappingId === vatRunData.mappingId
            )}
            jsonMapping={jsonMapping}
          />
        )}
      </>
    );
  };
  const tabsHeaderClass =
    menuSize === MenuSize.WIDE ? 'header-narrow' : 'header-wide';

  // Fix: Define layoutClass to avoid ReferenceError
  const layoutClass =
    menuSize === MenuSize.WIDE ? 'layout-narrow' : 'layout-wide';

  const downloadPDF = async () => {
    setExporting(true);
    const getTargetElement = document.getElementById('vat-to-be-exported');
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const getStatusElement = document.getElementById('vat-status');
      if (!getTargetElement || !getStatusElement) {
        return;
      }

      if (document.fonts?.ready !== undefined) {
        await document.fonts.ready;
      }

      getTargetElement.classList.add('generating-pdf');
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );

      // html2canvas and canvas 2d cannot parse CSS var(); resolve the token first.
      const pdfSurfaceColor =
        getComputedStyle(document.documentElement)
          .getPropertyValue('--color-surface-raised')
          .trim() || '#ffffff';

      // Canvas renderer paints text with fillText; it often disagrees with DOM layout for web fonts
      // (e.g. Inter) and synthetic weights (450). onclone restyles only the iframe clone — screen UI unchanged.
      const html2canvasOpts = {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: pdfSurfaceColor,
        onclone: (clonedDoc) => {
          const pdfFontFix = clonedDoc.createElement('style');
          pdfFontFix.textContent = `
            #vat-to-be-exported table td,
            #vat-to-be-exported table th {
              font-family: Arial, Helvetica, "Segoe UI", sans-serif !important;
              font-weight: 400 !important;
              letter-spacing: normal !important;
              word-spacing: normal !important;
              font-feature-settings: normal !important;
            }
            #vat-to-be-exported .vatreporttable thead tr,
            #vat-to-be-exported .vatreporttable thead th {
              font-weight: 600 !important;
            }
            #vat-to-be-exported .vatreporttable .box {
              font-family: Arial, Helvetica, "Segoe UI", sans-serif !important;
              font-weight: 700 !important;
            }
            #vat-to-be-exported .vatreporttable .subtotal,
            #vat-to-be-exported .vatreporttable .subtotal-2 {
              font-style: italic !important;
            }
          `;
          clonedDoc.head.appendChild(pdfFontFix);
        },
      };

      const [mainCanvas, statusCanvas] = await Promise.all([
        html2canvas(getTargetElement, html2canvasOpts),
        html2canvas(getStatusElement, html2canvasOpts),
      ]);

      const pdf = new jsPDF('p', 'mm', 'a4', true);
      const pageWmm = pdf.internal.pageSize.getWidth();
      const pageHmm = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const imgWidthMm = pageWmm - margin * 2;
      const headerContentStartMm = 40;
      const firstPageSliceMaxMm = pageHmm - headerContentStartMm - margin;
      const continuationSliceMaxMm = pageHmm - margin * 2;

      pdf.addImage(statusCanvas.toDataURL('image/png'), 'PNG', 182, 25, 24, 6);

      pdf.addImage(Logo, 'PNG', 5, 5, 45, 8);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor('#212529');
      pdf.text(`${vatRunData.connectionName}`, 100, 17, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(
        `${vatRunData.taxReportCountry} - ${vatRunData.taxReportTypeName} (${vatRunData.currency})`,
        100,
        22,
        { align: 'center' }
      );
      pdf.text(`Period - ${vatRunData.generationPeriod}`, 100, 27, {
        align: 'center',
      });
      pdf.text(`Generated - ${generatedDate}`, 100, 32, {
        align: 'center',
      });

      if (mainCanvas.width > 0 && mainCanvas.height > 0) {
        let sourceYPx = 0;
        let pageIdx = 0;

        while (sourceYPx < mainCanvas.height) {
          if (pageIdx > 0) {
            pdf.addPage();
          }

          const remainingPx = mainCanvas.height - sourceYPx;
          const maxSliceMm =
            pageIdx === 0 ? firstPageSliceMaxMm : continuationSliceMaxMm;
          const remainingMm = (remainingPx / mainCanvas.width) * imgWidthMm;
          const targetSliceMm = Math.min(maxSliceMm, remainingMm);
          let slicePx = Math.floor(
            (targetSliceMm / imgWidthMm) * mainCanvas.width
          );
          slicePx = Math.max(1, Math.min(slicePx, remainingPx));

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = mainCanvas.width;
          sliceCanvas.height = slicePx;
          const sctx = sliceCanvas.getContext('2d');
          if (sctx) {
            sctx.fillStyle = pdfSurfaceColor;
            sctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            sctx.drawImage(
              mainCanvas,
              0,
              sourceYPx,
              mainCanvas.width,
              slicePx,
              0,
              0,
              mainCanvas.width,
              slicePx
            );
            const drawHeightMm = (slicePx / mainCanvas.width) * imgWidthMm;
            const yPos = pageIdx === 0 ? headerContentStartMm : margin;
            pdf.addImage(
              sliceCanvas.toDataURL('image/png'),
              'PNG',
              margin,
              yPos,
              imgWidthMm,
              drawHeightMm
            );
          }

          sourceYPx += slicePx;
          pageIdx += 1;
        }
      }

      pdf.save(
        `${vatRunData.connectionName} - ${vatRunData.taxReportTypeName}.pdf`
      );
    } finally {
      getTargetElement?.classList.remove('generating-pdf');
      setExporting(false);
    }
  };

  const exportToExcel = () => {
    const selectedSubsections = switchTotals
      ? vatReportData?.adjustedSubsections
      : vatReportData?.subsections;

    let boxValues: BoxValue[] = [];

    if (selectedSubsections) {
      boxValues = selectedSubsections.map(({ code, net, tax, cellName }) => {
        if (
          vatRunData.taxReportCountry === TaxReportCountry.MALTA &&
          code === 'Box 45'
        ) {
          let box45 = 0;
          const box43 = vatReportData?.subsections?.find(
            (subs) => subs.code == 'Box 43'
          )?.net;
          const box44 = vatReportData?.subsections?.find(
            (subs) => subs.code == 'Box 44'
          )?.net;

          if (
            box43 !== undefined &&
            box44 !== undefined &&
            !isNaN(box43) &&
            !isNaN(box44)
          ) {
            box45 = box43 - box44;
          }

          return { code, net: box45, tax, cellName };
        } else {
          return { code, net, tax, cellName };
        }
      });
    }

    dispatch(
      exportToExcelRequest(
        reportId,
        vatRunData.mappingId,
        `${vatRunData.connectionName} - ${vatRunData.taxReportTypeName}.xlsx`,
        boxValues
      )
    );
  };

  const renderContextMenu = (onClose?: () => void) => {
    return (
      <div className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <IconContextButton
            type="text"
            icon={
              <FilePdfOutlined
                style={{ width: '18px', height: '19px', fontSize: 18 }}
              />
            }
            onClick={() => {
              downloadPDF();
              onClose?.();
            }}
          >
            {t('vat-report.export-pdf')}
          </IconContextButton>
          <IconContextButton
            //disabled
            type="text"
            icon={
              <FileExcelOutlined
                style={{ width: '18px', height: '19px', fontSize: 18 }}
              />
            }
            onClick={exportToExcel}
          >
            {t('vat-report.export-excel')}
          </IconContextButton>
        </Space>
      </div>
    );
  };

  const renderClientBox = () => {
    return (
      <>
        {showCard && (
          <StyledCard
            width={'100%'}
            $nopadding
            fullsize
            hideheader
            style={{
              //border: '1px solid #D13131',
              boxShadow: '0px 4px 10px 0px #0000000D',
              marginBottom: '20px',
              display: 'flex',
              fontSize: 14,
              fontWeight: 400,
              padding: 0,
            }}
            extra={
              <IconClose
                role="button"
                tabIndex={0}
                aria-label={t('common.close')}
                style={{
                  top: '25',
                  position: 'absolute',
                  right: '25',
                  cursor: 'pointer',
                  filter:
                    'invert(71%) sepia(12%) saturate(247%) hue-rotate(131deg) brightness(80%) contrast(81%)',
                }}
                onClick={() => setShowCard(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowCard(false);
                  }
                }}
              />
            }
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-evenly',
                flexFlow: 'row',
                alignContent: 'normal',
                padding: '24px 24px 0 0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexFlow: 'column',
                  padding: '30px 0px 24px 0px',
                }}
              >
                <Space direction="vertical">
                  <Space>
                    <CardTitleLargeSpan
                      style={{ marginBottom: 10, display: 'block' }}
                    >
                      {t('vat-report.review-pending')}
                    </CardTitleLargeSpan>
                    <ExclamationCircleOutlined
                      style={{
                        color: '#FFBC3E',
                        fontSize: 19,
                        marginLeft: 6,
                        marginBottom: 8,
                      }}
                    />
                  </Space>
                  <CardDescSpan style={{ marginBottom: 16 }}>
                    <BlackSpan>
                      {t('vat-report.invited-to-review-by')}{' '}
                      <BoldNameSpan style={{ fontWeight: 700 }}>
                        {vatRunData.preparer}
                      </BoldNameSpan>
                      .<br />
                      {t('vat-report.please-approve-or-reject')}
                    </BlackSpan>
                  </CardDescSpan>
                </Space>
              </div>

              <img
                alt={t('vat-report.vat-checker-image-alt')}
                height={154}
                src={PendingReview}
              />
            </div>
          </StyledCard>
        )}
      </>
    );
  };

  const renderConnectionWarning = (activeTabKey?: string) => {
    if (!currentOrganisation || currentOrganisation.connectedProperly) {
      return null;
    }

    return (
      <EntityNotConnectedPanel
        marginBottom={activeTabKey === '3' ? '40px' : '20px'}
        onReconnect={() => {
          dispatch(
            turnDrawerOn({
              title: t('organisation.add-new-entity'),
              subtitle: t('organisation.connect-entity-subtitle'),
              type: DrawerType.CONNECT_ENTITY,
              component: <ConnectEntity />,
            })
          );
        }}
      />
    );
  };

  const renderForms = () => {
    if (returnForm) {
      return returnForm;
    }
    if (vatRunData.taxType !== VatTaxReportType.RECAP) {
      // Fallback: if taxReportCountry missing, infer from report type name
      const typeName = vatRunData.taxReportTypeName?.toLowerCase() ?? '';
      const typeId = vatRunData.reportingTypeId ?? '';
      const taxReportCountry =
        vatRunData.taxReportCountry ||
        (typeName.includes('uk vat return')
          ? TaxReportCountry.UK
          : undefined) ||
        (typeName.includes('south africa') || typeName.includes('vat 201')
          ? TaxReportCountry.SOUTH_AFRICA
          : undefined) ||
        (isOssReturnType(typeId) ? TaxReportCountry.EUROPE : undefined) ||
        vatRunData.taxReportCountry;
      //const isUkReturn =
      taxReportCountry === TaxReportCountry.UK ||
        (typeName ?? '').includes('uk');
      const hasHmrcObligationError = Boolean(
        (vatRunData.obligation ?? []).find((item) => {
          const hasMessage = !!item?.message?.trim();
          const hasOnlyNullFields =
            item?.startDate === null &&
            item?.endDate === null &&
            item?.status === null &&
            item?.periodKey === null;
          return hasMessage && hasOnlyNullFields;
        })?.message
      );
      const canSubmitToHmrc =
        !checkClient() &&
        !hasHmrcObligationError &&
        AccountType[currentUser.accountType] !== AccountType.CLIENT &&
        !checkDisabled(
          DataLogActions.MARK_AS_SUBMITTED,
          vatRunData,
          currentUser
        );
      return (
        <>
          <VatReturnForms
            data={vatReportData}
            taxReportCountry={taxReportCountry}
            setActiveKey={setActiveKey}
            setFilteredBox={setFilteredBox}
            showBoxTransactions={!returnAndMappingOnly}
            resetValues={resetValues}
            resetFinished={resetFinished}
            renderClientBox={renderClientBox}
            renderContextMenu={renderContextMenu}
            currentUser={currentUser}
            isAdjustedReturn={isAdjustedReturn}
            switchTotals={switchTotals}
            setSwitchTotals={setSwitchTotals}
            reportId={reportId}
            mappingId={vatRunData.mappingId}
            exporting={exporting}
            comparedData={comparedReportData}
            clearComparison={clearComparison}
            canSubmitToHmrc={canSubmitToHmrc}
            onSubmitToHmrc={() =>
              handleMenuClick(DataLogActions.MARK_AS_SUBMITTED)
            }
            jsonMapping={jsonMapping}
            mappingSections={
              mappingSections ??
              currentOrganisation?.returns.find(
                (r) => r.mappingId === vatRunData.mappingId
              )?.sections
            }
            reportingTypeId={vatRunData.reportingTypeId}
            status={vatRunData.status}
          />
        </>
      );
    } else {
      if (
        vatRunData.taxReportCountry === TaxReportCountry.MALTA &&
        vatReportData
      )
        return renderMaltaRecap(
          vatReportData?.contacts ?? [],
          handlePreferences,
          vatRunData,
          menuSize,
          showSideMenu,
          pwaMobileNav
        );
      else if (vatRunData.taxReportCountry === TaxReportCountry.CYPRUS) {
        return renderCyprusRecap(
          vatReportData?.contacts ?? undefined,
          vatRunData,
          menuSize,
          showSideMenu,
          pwaMobileNav
        );
      } else if (
        vatRunData.taxReportCountry === TaxReportCountry.IRELAND_RECAP &&
        vatReportData
      ) {
        return renderMaltaRecap(
          vatReportData?.contacts ?? [],
          handlePreferences,
          vatRunData,
          menuSize,
          showSideMenu,
          pwaMobileNav
        );
      } else if (
        vatRunData.taxReportCountry === TaxReportCountry.NETHERLANDS &&
        vatReportData
      ) {
        return renderMaltaRecap(
          vatReportData?.contacts ?? [],
          handlePreferences,
          vatRunData,
          menuSize,
          showSideMenu,
          pwaMobileNav
        );
      }
    }
  };

  const checkClient = () => {
    if (
      AccountType[currentUser.accountType] === AccountType.CLIENT &&
      vatRunData.status &&
      DataLogStatus[vatRunData.status] !==
        DataLogStatus.AWAITING_CLIENT_APPROVAL //&&
      // vatRunData.clientId !== currentUser.accountUuid
    )
      return true;
    if (
      AccountType[currentUser.accountType] === AccountType.CLIENT &&
      vatRunData.status &&
      DataLogStatus[vatRunData.status] ===
        DataLogStatus.AWAITING_CLIENT_APPROVAL &&
      vatRunData.clientId !== currentUser.accountUuid
    )
      return true;
    return false;
  };

  /* const chechIfClientLocked = () => {
    return (
      subscriptionPlanTypes.find((subType) =>
        subType.name.toLowerCase().includes('small')
      )?.uuid == currentUser?.typeId
    );
  }; */

  const renderChromeExtensionCard = () => {
    return (
      <StyledCard
        style={{
          height: 'fit-content',
          marginTop: '20px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <img
            src={ChromeExtensionPic}
            alt={t('vat-report.chrome-extension-card-image-alt')}
          />

          <span style={{ fontSize: 18, fontWeight: 400, lineHeight: '24px' }}>
            {t('vat-report.chrome-extension-question-prefix')}{' '}
            <span style={{ color: 'var(--color-primary, #41aaaf)', fontWeight: 600 }}>
              {t('vat-report.chrome-extension-copy-in-bulk')}
            </span>{' '}
            {t('vat-report.chrome-extension-question-suffix')}
          </span>
          <a
            href={`https://chrome.google.com/webstore/detail/generate-tax-extension/${process.env.EXTENSION_ID}`}
            target="_blank"
            rel="noreferrer"
          >
            <ScopeButton
              width="100px"
              height="35px"
              fontSize="13px"
              lineheight="12px"
              type="dark-orange"
            >
              {t('common.install')}
            </ScopeButton>
          </a>
        </div>
      </StyledCard>
    );
  };

  const renderSideMenuContent = (options?: { omitMeta?: boolean }) => {
    const omitMeta = Boolean(options?.omitMeta);
    const selReturnTypeCountry = currentOrganisation?.returnTypesEdit?.find(
      (rt) => rt.id === returnTypeId
    )?.countryName;

    // Derive a header status for the VAT return:
    // - NOT_STARTED is the submissions-list state before a return exists; once
    //   report data is loaded on this page, show Draft (NOT_SAVED) instead.
    const runStatus = vatRunData?.status
      ? DataLogStatus[vatRunData.status] ?? vatRunData.status
      : undefined;
    const headerStatus =
      hasReportWorkflowContext(vatRunData, vatReportData, success) &&
      (!runStatus ||
        runStatus === DataLogStatus.NOT_STARTED ||
        runStatus === DataLogStatus.NOT_SAVED)
        ? DataLogStatus.NOT_SAVED
        : runStatus;

    const canShowWorkflowActions =
      !cannotSave &&
      hasReportWorkflowContext(vatRunData, vatReportData, success);

    const isUkReturn =
      vatRunData.taxReportCountry === TaxReportCountry.UK ||
      (vatRunData.taxReportTypeName ?? '').toLowerCase().includes('uk');
    const ukObligationMessage =
      isUkReturn &&
      (vatRunData.obligation ?? []).find((item) => {
        const hasMessage = !!item?.message?.trim();
        const hasOnlyNullFields =
          item?.startDate === null &&
          item?.endDate === null &&
          item?.status === null &&
          item?.periodKey === null;
        return hasMessage && hasOnlyNullFields;
      })?.message;
    const hasHmrcObligationError = !!ukObligationMessage;
    const hmrcSubmissionBlocked = hasHmrcObligationError;

    return (
      <>
            {!omitMeta ? (
            <>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div id="vat-status">
                <StatusGetter status={headerStatus} />
              </div>
              {!pwaMobileNav &&
                mappedForExtension &&
                DataLogStatus[vatRunData.status ?? ''] ===
                  DataLogStatus.SUBMITTED && (
                  <Tooltip
                    title={t('vat-report.copy-to-chrome-extension-tooltip')}
                  >
                    <ExportButton
                      icon={
                        <img
                          src={ChromeExtensions2Png}
                          alt={t('vat-report.chrome-extension-image-alt')}
                          width={20}
                          height={20}
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        />
                      }
                      onClick={() => {
                        handleExtensionCalls(COPY_TO_EXTENSION)
                          .then(() => {
                            const message: MessageData = {
                              title: t(
                                'vat-report.data-successfully-copied-title'
                              ),
                              description: (
                                <span>
                                  {t(
                                    'vat-report.all-values-copied-to-chrome-extension'
                                  )}
                                  <br />
                                  <br />
                                  {t(
                                    'vat-report.log-in-vat-portal-and-paste-data'
                                  )}
                                </span>
                              ),
                              type: MessageStates.SUCCESS,
                            };
                            dispatch(turnMessageOn(message));
                          })
                          .catch((error) => {
                            console.error('Error copying to extension:', error);
                            const message: MessageData = {
                              title: t('common.problem'),
                              description: t(
                                'vat-report.check-chrome-extension-installed-and-try-again'
                              ),
                              type: MessageStates.ERROR,
                            };
                            dispatch(turnMessageOn(message));
                          });
                      }}
                    >
                      {t('vat-report.copy-to-chrome-extension')}
                    </ExportButton>
                  </Tooltip>
                )}
            </div>
            <div className="divider-20" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ProviderLogoNav
                providerName={vatRunData?.providerName}
                shortCode={vatRunData.shortCode}
                organisationId={currentOrganisation?.organisationId}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                }}
              >
                {vatRunData?.providerName?.toLowerCase() === 'group' ? (
                  <UsergroupAddOutlined
                    style={{
                      fontSize: 20,
                      color: 'var(--color-on-logo-tile, #042a2a)',
                      paddingTop: '4px',
                    }}
                  />
                ) : (
                  <ProviderIcon
                    providerName={vatRunData?.providerName}
                    alt={vatRunData?.providerName}
                    width={25}
                  />
                )}
              </ProviderLogoNav>
              <span className="menu-entity-name">
                {vatRunData.connectionName}
                {vatRunData.taxReportTypeName
                  ? ` / ${vatRunData.taxReportTypeName}`
                  : ''}
              </span>
            </div>
            <div className="divider-10" />
            <div className="menu-entity-details">
              <span id="country">
                {localizeCountry(selReturnTypeCountry, selReturnTypeCountry)}
              </span>
              {vatRunData.currency ? ` (${vatRunData.currency})` : null}
              <br />
              <span id="period">{`Period - ${vatRunData.generationPeriod}`}</span>
              <br />
              {`Generated - ${generatedDate}`}
            </div>
            </>
            ) : null}

            {!omitMeta &&
            !returnForm &&
            !pwaMobileNav &&
            isChrome &&
            //mappedForExtension &&
            !foundChromeExtension &&
            DataLogStatus[vatRunData.status ?? ''] ===
              DataLogStatus.SUBMITTED ? (
              renderChromeExtensionCard()
            ) : omitMeta ? null : (
              <div
                className="divider-20"
                style={{ borderBottom: '1px solid var(--color-border, #dbeae3)' }}
              />
            )}
            {canShowWorkflowActions ? (
              /* (vatReportData.journals.length > 0 ||
              vatReportData.exceptions.length > 0) */ <>
                {!omitMeta &&
                  (!vatRunData.status ||
                    (DataLogStatus[vatRunData.status] !==
                      DataLogStatus.SUBMITTED &&
                      DataLogStatus[vatRunData.status] !==
                        DataLogStatus.CANCELLED)) &&
                  !hmrcSubmissionBlocked && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        //gap: 20,
                        margin: '20px 0',
                      }}
                    >
                      <Spin spinning={loading || !success}>
                        {getNextButton(
                          handleMenuClick,
                          vatRunData,
                          currentUser,
                          checkClient,
                          currentOrganisation,
                          hasAdvancedUserRights,
                          t('global.create-submission-rights-warning'),
                          t('global.approve-submission-rights-warning')
                        )}
                      </Spin>{' '}
                      {!checkClient() &&
                        !hmrcSubmissionBlocked &&
                        (!loading || success) &&
                        currentUser &&
                        AccountType[currentUser.accountType] !==
                          AccountType.CLIENT &&
                        (() => {
                          const isStaff =
                            AccountType[currentUser.accountType] ===
                            AccountType.STAFF;
                          const lacksCreateSubmissionRight =
                            isStaff &&
                            hasAdvancedUserRights &&
                            !!currentOrganisation &&
                            !hasAdvancedUserRight(
                              currentUser,
                              currentOrganisation as any,
                              AdvancedUserRightsCodes.CREATE_SUBMISSION
                            );
                          const button = (
                            <ContextButton
                              type="text" //@ts-ignore
                              bgcolor="white"
                              aria-label={t('common.more-actions')}
                            >
                              <EllipseIcon
                                aria-hidden
                                style={{
                                  width: 18,
                                  height: 18,
                                }}
                              />
                            </ContextButton>
                          );
                          return (
                            <Popover
                              placement="bottomRight"
                              arrow={false}
                              trigger="click"
                              destroyOnHidden={true}
                              content={getActionMenuItems(
                                vatRunData,
                                currentUser,
                                handleMenuClick,
                                cannotSave,
                                lacksCreateSubmissionRight,
                                t('global.action-rights-warning'),
                                (() => {
                                  const canCancel =
                                    !!currentOrganisation &&
                                    (!hasAdvancedUserRights ||
                                      hasAdvancedUserRight(
                                        currentUser,
                                        currentOrganisation as any,
                                        AdvancedUserRightsCodes.CANCEL_SUBMISSION
                                      ));
                                  const canApprove =
                                    !!currentOrganisation &&
                                    (!hasAdvancedUserRights ||
                                      hasAdvancedUserRight(
                                        currentUser,
                                        currentOrganisation as any,
                                        AdvancedUserRightsCodes.APPROVE_SUBMISSION
                                      ));
                                  const actions: DataLogActions[] = [];
                                  if (canCancel)
                                    actions.push(DataLogActions.CANCEL_RETURN);
                                  if (canApprove) {
                                    actions.push(
                                      DataLogActions.SET_INTERNAL_APPROVED
                                    );
                                    actions.push(
                                      DataLogActions.SET_NOT_APPROVED
                                    );
                                    actions.push(
                                      DataLogActions.LOCK_AS_APPROVED
                                    );
                                  }

                                  return actions;
                                })()
                              )}
                              title={undefined}
                              overlayStyle={{ zIndex: 999 }}
                              overlayClassName="scope-context-menu-popover"
                            >
                              {button}
                            </Popover>
                          );
                        })()}
                    </div>
                  )}
                {ukObligationMessage && (
                  <ErrorWrapper>
                    <span className="error-text">{ukObligationMessage}</span>
                  </ErrorWrapper>
                )}
                {vatReportData?.hasReconciliationExceptions && (
                  <ErrorWrapper>
                    <span className="error-text">
                      {t(
                        'global.reconciliation-exceptions-out-of-scope-warning'
                      )}
                    </span>
                    {(vatReportData?.incorrectTaxRates ?? []).length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <span
                          className="error-text"
                          style={{ fontWeight: 600 }}
                        >
                          {t('vat-report.affected-tax-rates')}{' '}
                        </span>
                        <span className="error-text">
                          {(vatReportData?.incorrectTaxRates ?? []).join(', ')}
                        </span>
                      </div>
                    )}
                  </ErrorWrapper>
                )}

                {vatRunData.dataLogId &&
                  vatRunData.status &&
                  DataLogStatus[vatRunData.status] !==
                    DataLogStatus.CANCELLED &&
                  (() => {
                    const isStaff =
                      AccountType[currentUser.accountType] ===
                      AccountType.STAFF;
                    const lacksCreateSubmissionRight =
                      isStaff &&
                      hasAdvancedUserRights &&
                      !!currentOrganisation &&
                      !hasAdvancedUserRight(
                        currentUser,
                        currentOrganisation as any,
                        AdvancedUserRightsCodes.CREATE_SUBMISSION
                      );
                    const tab = (
                      <ActivityTab
                        data={vatRunData}
                        updateActivityRole={updateActivityRole}
                        activityLogs={activityLogs}
                        updateNote={updateNote}
                        currentUser={currentUser}
                        isClientLocked={lockClient}
                        sheetMode={omitMeta}
                      />
                    );
                    return lacksCreateSubmissionRight ? (
                      <StyledTooltip
                        title={t('global.create-submission-rights-warning')}
                        placement="left"
                        arrow={false}
                      >
                        <span
                          style={{ display: 'inline-block', width: '100%' }}
                        >
                          <div style={{ pointerEvents: 'none', opacity: 0.5 }}>
                            {tab}
                          </div>
                        </span>
                      </StyledTooltip>
                    ) : (
                      tab
                    );
                  })()}
              </>
            ) : null}
            {cannotSave && vatReportData?.hasReconciliationExceptions && (
              <ErrorWrapper>
                <span className="error-text">
                  {t('global.reconciliation-exceptions-out-of-scope-warning')}
                </span>
                {(vatReportData?.incorrectTaxRates ?? []).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <span className="error-text" style={{ fontWeight: 600 }}>
                      {t('vat-report.affected-tax-rates')}{' '}
                    </span>
                    <span className="error-text">
                      {(vatReportData?.incorrectTaxRates ?? []).join(', ')}
                    </span>
                  </div>
                )}
              </ErrorWrapper>
            )}
            {renderErrorMessage(vatRunData)}
      </>
    );
  };

  const renderSideMenu = () => {
    if (pwaMobileNav || !showSideMenu) {
      return null;
    }
    return <div className="menu-wrapper">{renderSideMenuContent()}</div>;
  };

  const openPwaActivityLogs = () => {
    if (!vatRunData?.dataLogId) {
      return;
    }
    dispatch(
      turnDrawerOn({
        title: t('organisation.reconciliation-activity'),
        type: DrawerType.ACTIVITY_LOGS,
        component: (
          <ActivityLogs
            data={vatRunData}
            activityLogs={activityLogs}
            updateNote={updateNote}
            connectionId={vatRunData.tokenId}
            mappingId={vatRunData.mappingId}
          />
        ),
      })
    );
  };

  const renderPwaSideMenuOverlay = () => {
    if (!pwaMobileNav || !showSideMenu) {
      return null;
    }

    const canViewActivity =
      !!vatRunData?.dataLogId &&
      !!vatRunData?.status &&
      DataLogStatus[vatRunData.status] !== DataLogStatus.CANCELLED &&
      !!currentUser?.accountType &&
      AccountType[currentUser.accountType] !== AccountType.CLIENT;

    return createPortal(
      <>
        <button
          type="button"
          className="scope-pwa-vat-report-menu-backdrop"
          aria-label={t('common.close')}
          onClick={closePwaSideMenu}
        />
        <div
          className="scope-pwa-vat-report-menu scope-pwa-vat-report-menu--dock scope-pwa-bottom-sheet"
          data-shell="pwa"
          role="dialog"
          aria-modal="true"
          aria-label={t('vat-report.return-workflow', {
            defaultValue: 'Return workflow',
          })}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            ref={pwaMenuDragZoneRef}
            className="scope-pwa-vat-report-menu__chrome scope-pwa-bottom-sheet__drag-zone"
            aria-label={t('organisation.pull-down-to-close')}
            {...pwaMenuDragClose}
          >
            <div
              className="scope-pwa-bottom-sheet__drag-handle"
              aria-hidden
            />
            <div className="scope-pwa-vat-report-menu__header">
              <div className="scope-pwa-vat-report-menu__titles">
                <strong>
                  {t('vat-report.return-workflow', {
                    defaultValue: 'Return workflow',
                  })}
                </strong>
                <span>
                  {[
                    vatRunData.connectionName,
                    vatRunData.taxReportTypeName,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
              <button
                type="button"
                className="scope-pwa-vat-report-menu__close"
                onClick={closePwaSideMenu}
                onPointerDown={(event) => event.stopPropagation()}
                aria-label={t('common.close')}
              >
                <IconClose aria-hidden />
              </button>
            </div>
          </div>
          <div className="scope-pwa-vat-report-menu__body">
            <div className="scope-pwa-vat-report-menu__summary">
              <div className="scope-pwa-vat-report-menu__summary-top">
                <StatusGetter
                  status={
                    DataLogStatus[vatRunData.status ?? ''] ||
                    vatRunData.status
                  }
                  noDraft
                  smaller
                />
                {generatedDate || vatRunData.generatedDate ? (
                  <span className="pwa-row-meta">
                    {t('organisation.pwa-return-generated', {
                      defaultValue: 'Generated {{date}}',
                      date:
                        generatedDate ||
                        formatDisplayDate(
                          vatRunData.generatedDate,
                          'D MMM YYYY, HH:mm',
                          i18n.language
                        ),
                    })}
                  </span>
                ) : null}
              </div>
              <dl className="scope-pwa-vat-report-menu__kv">
                <div>
                  <dt>
                    {t('organisation.jurisdiction', {
                      defaultValue: 'Jurisdiction',
                    })}
                  </dt>
                  <dd>
                    {[
                      localizeCountry(
                        currentOrganisation?.returnTypesEdit?.find(
                          (rt) => rt.id === returnTypeId
                        )?.countryName,
                        String(vatRunData.taxReportCountry || '')
                      ),
                      vatRunData.currency
                        ? `(${vatRunData.currency})`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </dd>
                </div>
                <div>
                  <dt>{t('main.period', { defaultValue: 'Period' })}</dt>
                  <dd>
                    {vatRunData.fromDate && vatRunData.toDate
                      ? formatDisplayDateRange(
                          vatRunData.fromDate,
                          vatRunData.toDate,
                          'D MMM YYYY',
                          i18n.language
                        )
                      : vatRunData.generationPeriod || '—'}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="scope-pwa-vat-report-menu__workflow">
              {renderSideMenuContent({ omitMeta: true })}
            </div>
          </div>
          {canViewActivity ? (
            <div className="scope-pwa-vat-report-menu__footer">
              <button
                type="button"
                className="pwa-btn secondary block scope-pwa-vat-report-menu__activity"
                onClick={openPwaActivityLogs}
              >
                <PwaIcon name="history" size={18} />
                <span>
                  {t('vat-report.view-activity', {
                    defaultValue: 'View activity',
                  })}
                </span>
              </button>
            </div>
          ) : null}
        </div>
      </>,
      document.body
    );
  };

  const filterJournals = () => {
    const filteredData = vatReportData?.journals?.map((value, index) => {
      value.key = index + 1;
      return value;
    });
    return filteredData;
  };

  /* const RenderLockedExceptions = () => {
    return (
      <StyledCard width="980px">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexFlow: 'row',
            alignContent: 'normal',
            columnGap: 40,
          }}
        >
          <div
            style={{
              padding: '20px 0px',
              paddingTop: '48px',
              paddingLeft: '95px',
            }}
          >
            <Typography
              style={{
                fontSize: '24px',
                fontWeight: '600',
                lineHeight: '26px',
                textAlign: 'left',
                color: 'var(--color-text-dark, #042a2a)',
                marginBottom: '10px',
              }}
            >
              <CardDescGreenSpan fontSize="32px">
                Unlock Exceptions
              </CardDescGreenSpan>
              .
              <br />
              Lorem ipsum title goes here <br />
            </Typography>
            <br />
            <CardDescSpan>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed
              sollicitudin risus leo, id viverra mi accumsan vel. In nec nisi
              nec purus euismod hendrerit a in tellus. Suspendisse facilisis
              tincidunt lorem imperdiet rhoncus. Phasellus sollicitudin dolor
              sit amet nunc venenatis, et mollis urna efficitur.
            </CardDescSpan>
            <br />
            <br />
            <List
              style={{ marginBottom: '15px' }}
              dataSource={[
                'More VAT Functionality',
                'Connect more entities',
                'Integrations with Xero, Excel and more!',
              ]}
              split={false}
              size="small"
              renderItem={(item) => (
                <List.Item style={{ padding: '0px 3px' }}>
                  <CheckIcon></CheckIcon> <BlackSpan>{item}</BlackSpan>
                </List.Item>
              )}
            />

            <YellowButton
              type="primary"
              width="139px"
              height="32"
              fontSize="13px"
              className="yellow-btn"
              onClick={() => redirectPage(billingPageLink)}
            >
              Activate Now
            </YellowButton>
          </div>
          <img
            alt="upgrade-pic"
            style={{ width: '404px' }}
            src={UnlockExceptions}
          />
        </div>
      </StyledCard>
    );
  }; */

  const addTabItems = (reportData?: VatReportData): any => {
    const wrapTab = (
      id: string,
      label: React.ReactNode,
      child: React.ReactNode
    ) => ({
      disabled: loading || !success,
      label,
      key: id,
      children: (
        <>
          <div
            className={`content-wrapper vat-content ${tabsHeaderClass} scroller-submissions`}
          >
            <div className="vat-report-main">
              {!currentOrganisation?.connectedProperly &&
                renderConnectionWarning(id)}
              {child}
              <div className="divider-20" />
              <div className="divider-20" />
              <div className="divider-20" />
            </div>
            {renderSideMenu()}
          </div>
        </>
      ),
    });

    if (returnAndMappingOnly) {
      const isRecapStatement =
        vatRunData.taxType === VatTaxReportType.RECAP &&
        (vatRunData.taxReportCountry === TaxReportCountry.MALTA ||
          vatRunData.taxReportCountry === TaxReportCountry.CYPRUS ||
          vatRunData.taxReportCountry === TaxReportCountry.IRELAND_RECAP ||
          vatRunData.taxReportCountry === TaxReportCountry.NETHERLANDS);
      return [
        wrapTab(
          '1',
          isRecapStatement
            ? t('vat-report.statement')
            : t('vat-report.vat-return'),
          <>{renderForms()}</>
        ),
        wrapTab('2', t('transactions.mapping'), mappingTab()),
      ];
    }

    const items = new Array(pages).fill(null).map((_, i) => {
      const id = i + 1;
      let lbl;
      let child;
      switch (id) {
        case 1:
          child = (
            <>
              {pwaMobileNav ? renderPwaReturnIntro() : null}
              {renderForms()}
            </>
          );
          if (vatRunData.taxType !== VatTaxReportType.RECAP) {
            lbl = pwaMobileNav
              ? t('organisation.pwa-return-tab', { defaultValue: 'Return' })
              : t('vat-report.vat-return');
            break;
          } else {
            if (
              vatRunData.taxReportCountry === TaxReportCountry.MALTA ||
              vatRunData.taxReportCountry === TaxReportCountry.CYPRUS ||
              vatRunData.taxReportCountry === TaxReportCountry.IRELAND_RECAP ||
              vatRunData.taxReportCountry === TaxReportCountry.NETHERLANDS
            ) {
              lbl = pwaMobileNav
                ? t('organisation.pwa-statement-tab', {
                    defaultValue: 'Statement',
                  })
                : t('vat-report.statement');
              break;
            }
          }
        case 2:
          const count = vatReportData?.transactionsSize
            ? vatReportData.transactionsSize
            : 0;

          // Check if we should show the warning panel (hide count when warning is shown)
          // Show warning only when BOTH transactions AND exceptions are empty
          const shouldShowWarning =
            vatRunData.status &&
            (DataLogStatus[vatRunData.status] === DataLogStatus.SUBMITTED ||
              DataLogStatus[vatRunData.status] === DataLogStatus.CANCELLED) &&
            (!filterJournals() || filterJournals()?.length === 0) &&
            (!vatReportData?.exceptions ||
              vatReportData.exceptions.length === 0);

          lbl = pwaMobileNav ? (
            <span className="scope-pwa-vat-report-tab-label">
              {t('organisation.transactions')}
              <span className="count">
                {formatDisplayInteger(count ? count : 0, i18n.language)}
              </span>
            </span>
          ) : shouldShowWarning ? (
            t('organisation.transactions')
          ) : (
            `${t('organisation.transactions')} (${formatDisplayInteger(
              count ? count : 0,
              i18n.language
            )})`
          );
          child = (
            <>
              <TransactionTable
                loading={loading}
                attachmentLoading={attachmentLoading}
                // merged data from billing--- Delete when sure
                /* transData={filterJournals(filteredBox)}
                dataToExport={filterJournals(filteredBox)}
                journalType={journalType}
                setJournalType={setJournalType}
                filteredBox={filteredBox}
                noSumData={
                  vatReportData && vatReportData.summarizedJournals.length === 0
                }
                handlePreferences={handlePreferences}
                providerName={vatRunData.providerName}
                showSideMenu={showSideMenu}
                isAdjustedReturn={!!vatReportData?.adjustedSubsections}
                menuSize={menuSize}
                connectionId={vatRunData.tokenId}
                setShowSideMenu={setShowSideMenu} */
                transData={filterJournals()}
                journalType={journalType}
                setJournalType={setJournalType}
                filteredBox={filteredBox}
                noSumData={(vatReportData?.summarizedSize ?? 0) === 0}
                summarizedStatusPending={summarizedStatusPending}
                handlePreferences={handlePreferences}
                providerName={vatRunData.providerName}
                showSideMenu={showSideMenu}
                isAdjustedReturn={isAdjustedReturn}
                menuSize={menuSize}
                connectionId={vatRunData.tokenId}
                setShowSideMenu={setShowSideMenu}
                reportId={reportId}
                total={vatReportData?.transactionsSize}
                connectedProperly={currentOrganisation?.connectedProperly}
                status={vatRunData.status}
                vatRunData={vatRunData}
                vatReportData={vatReportData}
                onLoadReport={() => {
                  const vatLoadRequestData = {
                    connectionId: vatRunData.tokenId,
                    fromDate: vatRunData.fromDate,
                    toDate: vatRunData.toDate,
                    currency: vatRunData.currency,
                    mappingId: vatRunData.mappingId,
                    dataLogId: vatRunData.dataLogId,
                    provider: vatRunData.providerName?.toLowerCase(),
                  };
                  dispatch(loadReportRequest(vatLoadRequestData, reportId));
                }}
                currentSubscription={currentSubscription}
                currentUser={currentUser}
                currentOrganisation={currentOrganisation}
                hasAdvancedUserRights={hasAdvancedUserRights}
              />
            </>
          );
          break;
        case 3:
          const handleExceptions =
            !!vatRunData.dataLogId &&
            vatRunData.status &&
            DataLogStatus[vatRunData.status] !== DataLogStatus.CANCELLED &&
            DataLogStatus[vatRunData.status] !== DataLogStatus.NOT_SAVED &&
            DataLogStatus[vatRunData.status] !== DataLogStatus.LOCKED &&
            DataLogStatus[vatRunData.status] !== DataLogStatus.SUBMITTED; //&&
          //DataLogStatus[vatRunData.status] !==
          //DataLogStatus.AWAITING_APPROVAL; //&&
          //DataLogStatus[vatRunData.status] !==
          //DataLogStatus.AWAITING_CLIENT_APPROVAL;
          //DataLogStatus[vatRunData.status] !== DataLogStatus.INTERNAL_APPROVED &&
          //DataLogStatus[vatRunData.status] !== DataLogStatus.EXCEPTIONS_MODIFIED &&
          //DataLogStatus[vatRunData.status] !== DataLogStatus.CLIENT_APPROVED &&
          //DataLogStatus[vatRunData.status] !== DataLogStatus.APPROVED &&
          //DataLogStatus[vatRunData.status] !== DataLogStatus.SAVED

          if (pages === 4) {
            // Check if we should show the warning panel (hide count when warning is shown)
            // Show warning only when BOTH transactions AND exceptions are empty
            const shouldShowWarning =
              vatRunData.status &&
              (DataLogStatus[vatRunData.status] === DataLogStatus.SUBMITTED ||
                DataLogStatus[vatRunData.status] === DataLogStatus.CANCELLED) &&
              (!filterJournals() || filterJournals()?.length === 0) &&
              (!vatReportData?.exceptions ||
                vatReportData.exceptions.length === 0);

            const exceptionsCount = vatReportData?.exceptionsSize
              ? vatReportData.exceptionsSize
              : 0;
            lbl = pwaMobileNav ? (
              <span className="scope-pwa-vat-report-tab-label">
                {t('vat-report.exceptions')}
                <span className="count">
                  {formatDisplayInteger(exceptionsCount, i18n.language)}
                </span>
              </span>
            ) : shouldShowWarning ? (
              t('vat-report.exceptions')
            ) : (
              `${t('vat-report.exceptions')} (${formatDisplayInteger(
                exceptionsCount,
                i18n.language
              )})`
            );

            child = (
              <RenderExceptions
                transData={exceptionsSorted}
                openAdjustmentPanal={openAdjustmentPanal}
                addExceptionAction={addExceptionAction}
                handleExceptions={!!handleExceptions}
                handlePreferences={handlePreferences}
                providerName={vatRunData.providerName}
                showSideMenu={showSideMenu}
                menuSize={menuSize}
                reportId={reportId}
                total={vatReportData?.exceptionsSize}
                status={vatRunData.status}
                //vatRunData={vatRunData}
                vatReportData={vatReportData}
                onLoadReport={() => {
                  const vatLoadRequestData = {
                    connectionId: vatRunData.tokenId,
                    fromDate: vatRunData.fromDate,
                    toDate: vatRunData.toDate,
                    currency: vatRunData.currency,
                    mappingId: vatRunData.mappingId,
                    dataLogId: vatRunData.dataLogId,
                    provider: vatRunData.providerName?.toLowerCase(),
                  };
                  dispatch(loadReportRequest(vatLoadRequestData, reportId));
                }}
                lockExceptions={lockExceptions}
                currentOrganisation={currentOrganisation}
                providerShortCode={vatRunData.shortCode}
              />
            );
            break;
          } else {
            lbl = t('transactions.mapping');
            child = mappingTab();
            break;
          }

        case 4:
          lbl = t('transactions.mapping');
          child = mappingTab();
          break;
      }
      return {
        disabled: loading || !success,
        label: lbl,
        key: id.toString(),
        //children: child,
        children: (
          <>
            <div
              className={`content-wrapper vat-content ${tabsHeaderClass} scroller-submissions`}
            >
              <div className="vat-report-main">
                {!currentOrganisation?.connectedProperly &&
                  renderConnectionWarning(id.toString())}
                {child}
                <div className="divider-20" />
                <div className="divider-20" />
                <div className="divider-20" />
              </div>
              {renderSideMenu()}
            </div>
          </>
        ),
      };
    });

    // Phone/PWA: Mapping is too dense — keep Return / Transactions / Exceptions only.
    if (!pwaMobileNav) return items;
    const mappingKey = pages === 4 ? '4' : pages === 3 ? '3' : null;
    if (!mappingKey) return items;
    return items.filter((item) => item.key !== mappingKey);
  };

  const handleMenuClick = (key: DataLogActions) => {
    switch (key) {
      case DataLogActions.REVERT_DATA:
        revertData(vatRunData);
        break;
      case DataLogActions.SAVE_VAT_RETURN:
        saveReturn(vatRunData);
        break;
      case DataLogActions.CANCEL_RETURN:
        if (!vatRunData.status) cancelNotSaved(vatRunData);
        else cancelReturn(vatRunData);
        break;
      case DataLogActions.SET_AWAITING_APPROVAL:
        setAwaitingApproval(vatRunData);
        break;
      case DataLogActions.SET_INTERNAL_APPROVED:
        setInternallyApproved(vatRunData);
        break;
      case DataLogActions.SEND_FOR_CLIENT_APPROVAL:
        sendForClientApproval(vatRunData);
        break;
      case DataLogActions.SET_NOT_APPROVED:
        setNotApproved();
        break;
      case DataLogActions.SET_CLIENT_APPROVED:
        setClientApproved(vatRunData);
        break;
      case DataLogActions.MARK_AS_SUBMITTED:
        loackWarningWindow(vatRunData, DataLogActions.MARK_AS_SUBMITTED);
        break;
      case DataLogActions.LOCK_AS_APPROVED:
        loackWarningWindow(vatRunData, DataLogActions.LOCK_AS_APPROVED);
        break;
    }
  };

  const refreshDisable = (data: VatRunData) => {
    const disable =
      (data.status &&
        (DataLogStatus[data.status] === DataLogStatus.LOCKED ||
          DataLogStatus[data.status] === DataLogStatus.SUBMITTED ||
          DataLogStatus[data.status] === DataLogStatus.CANCELLED ||
          DataLogStatus[data.status] === DataLogStatus.INTERNAL_APPROVED)) ||
      (data.validRange !== null &&
        data.validRange === false &&
        data.isFirstTime === false);

    return disable;
  };

  const refreshTooltip = (data: VatRunData) => {
    let tooltip = t('vat-report.refresh-data-from-provider', {
      providerName: vatRunData.providerName,
    });
    const disable =
      (data.status &&
        (DataLogStatus[data.status] === DataLogStatus.LOCKED ||
          DataLogStatus[data.status] === DataLogStatus.SUBMITTED ||
          DataLogStatus[data.status] === DataLogStatus.CANCELLED ||
          DataLogStatus[data.status] === DataLogStatus.INTERNAL_APPROVED)) ||
      (data.validRange !== null &&
        data.validRange === false &&
        data.isFirstTime === false);

    if (disable) {
      if (
        data.status &&
        DataLogStatus[data.status] === DataLogStatus.SUBMITTED
      ) {
        tooltip = t('vat-report.refresh-disabled-submitted');
      } else if (
        data.status &&
        DataLogStatus[data.status] === DataLogStatus.LOCKED
      ) {
        tooltip = t('vat-report.refresh-disabled-locked');
      } else if (
        data.status &&
        DataLogStatus[data.status] === DataLogStatus.INTERNAL_APPROVED
      ) {
        tooltip = t('vat-report.refresh-disabled-internal-approved');
      } else {
        tooltip = t('vat-report.refresh-locked-return');
      }
    } else if (!currentOrganisation?.connectedProperly) {
      tooltip = t('vat-report.refresh-entity-not-connected');
    }
    return tooltip;
  };

  const onRefreshClick = (data: VatRunData) => {
    let isSaved = true;
    if (
      (data.validRange !== null &&
        data.validRange === true &&
        !data.dataLogId) ||
      data.isFirstTime === true
    ) {
      isSaved = false;
    }
    //setProgress(1);
    reloadReportData(data, true, true);
  };

  const showRefreshButton = () => {
    if (
      (!vatRunData.status ||
        DataLogStatus[vatRunData.status] === DataLogStatus.NOT_SAVED) &&
      ((vatRunData.validRange && vatRunData.validRange == true) ||
        vatRunData.isFirstTime)
    )
      return true;
    if (
      vatRunData.status &&
      DataLogStatus[vatRunData.status] === DataLogStatus.SAVED &&
      AccountType[currentUser.accountType] !== AccountType.CLIENT
    )
      return true;
    if (
      vatRunData.status &&
      DataLogStatus[vatRunData.status] === DataLogStatus.INTERNAL_APPROVED &&
      AccountType[currentUser.accountType] !== AccountType.CLIENT
    )
      return true;
    if (
      (vatRunData.status &&
        (DataLogStatus[vatRunData.status] === DataLogStatus.LOCKED ||
          DataLogStatus[vatRunData.status] === DataLogStatus.SUBMITTED ||
          DataLogStatus[vatRunData.status] === DataLogStatus.CANCELLED)) ||
      (vatRunData.validRange === false && vatRunData.isFirstTime === false)
    )
      return true;
    return false;
  };

  const onTabChange = (key) => {
    if (key == '1') setFilteredBox(null);
    setActiveKey(key);
  };

  const renderPwaActionsToolbar = () => {
    if (!pwaMobileNav) {
      return null;
    }

    const showProgress =
      loadingPercent !== 100 && progress !== 100 && !noDataFound;

    // Compare / Refresh / Export stay desktop-only — they crowd the Transactions tab on phone.
    if (!showProgress) {
      return null;
    }

    return (
      <div className="menu-top-wrapper scope-pwa-vat-report-toolbar">
        <div className="scope-pwa-vat-report-toolbar__progress">
          <div className="menu-entity-details">{progressText}</div>
          <Progress
            percent={progress}
            type="line"
            strokeColor="var(--color-primary, #41aaaf)"
          />
        </div>
      </div>
    );
  };

  const renderReportTopToolbar = () => {
    if (pwaMobileNav) {
      return null;
    }

    return (
    <div className="menu-top-wrapper">
      {loadingPercent === 100 && progress === 100 && !backendError && (
        <>
          <div
            style={{
              float: 'right',
              display: 'flex',
              alignItems: 'center',
              gap: '13px',
              justifyContent: 'end',
            }}
          >
            {(vatRunData.taxReportCountry ===
              TaxReportCountry.IRELAND_STANDARD ||
              vatRunData.taxReportCountry ===
                TaxReportCountry.IRELAND_RECAP ||
              (vatRunData.taxReportCountry === TaxReportCountry.MALTA &&
                vatRunData.taxType === VatTaxReportType.RECAP)) &&
              vatReportData?.contacts &&
              vatReportData?.contacts.length > 0 && (
                <>
                  {vatRunData.taxReportCountry === TaxReportCountry.MALTA &&
                    vatRunData.taxType === VatTaxReportType.RECAP && (
                      <ExportButton
                        icon={<DownloadOutlined />}
                        onClick={() => onDownloadCSVClick()}
                        loading={loading || !success}
                        style={{ zIndex: 200 }}
                      >
                        {t('vat-report.export-csv')}
                      </ExportButton>
                    )}
                  {!(
                    vatRunData.taxReportCountry === TaxReportCountry.MALTA &&
                    vatRunData.taxType === VatTaxReportType.RECAP
                  ) && (
                    <ExportButton
                      icon={<DownloadOutlined />}
                      onClick={() => onDownloadXMLClick()}
                      loading={loading || !success}
                      style={{ zIndex: 200 }}
                    >
                      {t('vat-report.export-xml')}
                    </ExportButton>
                  )}
                </>
              )}

            {onCompareReturn &&
              vatReturnLogs &&
              vatRunData &&
              vatRunData.taxType !== VatTaxReportType.RECAP && (
                <StyledTooltip // @ts-ignore
                  title={
                    isCompareReturnLocked
                      ? t('vat-report.unlock-compare-return')
                      : null
                  }
                  placement="bottom"
                  arrow={false}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {isCompareReturnLocked ? (
                      <ExportButton
                        onClick={handleCompareReturnUpgradeModal}
                        style={{ zIndex: 200, opacity: 0.6 }}
                      >
                        {t('vat-report.compare-return')}{' '}
                        <LockOutlined
                          style={{
                            marginLeft: '8px',
                            color: 'var(--color-text-muted, #5a6a6a)',
                          }}
                        />
                      </ExportButton>
                    ) : (
                      <CompareReturnModal
                        visible={showCompareModal}
                        onClose={() => {
                          setShowCompareModal(false);
                        }}
                        onSelect={(dataLogId) => {
                          onCompareReturn(dataLogId);
                          setShowCompareModal(false);
                        }}
                        vatReturnLogs={vatReturnLogs}
                        currentDataLogId={vatRunData.dataLogId}
                        connectionId={vatRunData.tokenId}
                        mappingId={vatRunData.mappingId}
                        currentUser={currentUser}
                        trigger={
                          <StyledTooltip
                            title={t('vat-report.compare-return-tooltip')}
                            placement="bottom"
                          >
                            <ExportButton
                              onClick={() => setShowCompareModal(true)}
                              style={{ zIndex: 200 }}
                            >
                              {t('vat-report.compare-return')}
                            </ExportButton>
                          </StyledTooltip>
                        }
                      />
                    )}
                  </div>
                </StyledTooltip>
              )}
            <StyledTooltip //@ts-ignore
              title={showRefreshButton() && refreshTooltip(vatRunData)}
              placement={'bottom'}
            >
              <ExportButton
                icon={
                  <IconRefresh
                    style={{ width: 16, height: 16, display: 'block' }}
                  />
                }
                disabled={
                  refreshDisable(vatRunData) ||
                  !showRefreshButton() ||
                  AccountType[currentUser.accountType] === AccountType.CLIENT
                }
                onClick={() =>
                  currentOrganisation?.connectedProperly
                    ? onRefreshClick(vatRunData)
                    : onReconnect()
                }
                loading={loading || !success}
                style={{
                  zIndex: 200,
                  ...(refreshDisable(vatRunData) && {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                  }),
                }}
              >
                {currentOrganisation?.connectedProperly
                  ? t('vat-report.refresh-data')
                  : t('common.reconnect')}
              </ExportButton>
            </StyledTooltip>
          </div>
        </>
      )}
      {loadingPercent !== 100 && progress !== 100 && !noDataFound && (
        <div style={{ width: pwaMobileNav ? '100%' : 220 }}>
          <div className="menu-entity-details">{progressText}</div>
          <Progress
            percent={progress}
            type={'line'}
            strokeColor="var(--color-primary, #41aaaf)"
          />
        </div>
      )}
      {currentOrganisation &&
        AccountType[currentUser.accountType] !== AccountType.CLIENT && (
          <AvatarGroupRenderer
            entity={currentOrganisation}
            maxCount={4}
            size={AvatarSizes.DEFAULT}
            expandToggleLeft
          />
        )}

      <div className="menu-burger-button">
        <div
          className={`menu-burger-icon ${
            showSideMenu ? 'burger-on' : 'burger-off'
          }`}
          onClick={() => setShowSideMenu(!showSideMenu)}
        >
          <MenuOutlined />
        </div>
        <span className="menu-burger-text">
          {showSideMenu
            ? t('vat-report.hide-menu')
            : t('vat-report.show-menu')}
        </span>
      </div>
    </div>
    );
  };

  const renderPwaReturnIntro = () => {
    if (!vatRunData?.status) return null;
    const statusValue =
      DataLogStatus[vatRunData.status] || String(vatRunData.status);
    const tone =
      statusValue === DataLogStatus.SUBMITTED
        ? 'ok'
        : statusValue === DataLogStatus.CANCELLED
          ? 'danger'
          : statusValue === DataLogStatus.AWAITING_APPROVAL ||
              statusValue === DataLogStatus.AWAITING_CLIENT_APPROVAL
            ? 'warn'
            : 'brand';
    const icon =
      tone === 'ok' ? 'check' : tone === 'danger' ? 'close' : tone === 'warn' ? 'review' : 'returns';
    const preparer = String(vatRunData.preparer ?? '').trim();
    const initials = preparer
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();

    return (
      <div className="scope-pwa-vat-return-intro">
        <button
          type="button"
          className="scope-pwa-vat-report-status-card pwa-card"
          onClick={() => setShowSideMenu(true)}
        >
          <PwaChip tone={tone} icon={icon}>
            {statusValue}
          </PwaChip>
          <span className="pwa-row-body">
            <span className="pwa-row-sub">
              {vatRunData.generatedDate
                ? t('organisation.pwa-return-generated', {
                    defaultValue: 'Generated {{date}}',
                    date: formatDisplayDate(
                      vatRunData.generatedDate,
                      'D MMM YYYY, HH:mm',
                      i18n.language
                    ),
                  })
                : null}
            </span>
          </span>
          {initials ? <PwaUserPic initials={initials} size="sm" /> : null}
          <PwaIcon name="chevron" size={18} />
        </button>
        {isAdjustedReturn ? (
          <PwaSegmented
            label={t('vat-report.figures', { defaultValue: 'Figures' })}
            value={switchTotals ? 'final' : 'original'}
            onChange={(value) => setSwitchTotals(value === 'final')}
            options={[
              {
                value: 'original',
                label: t('vat-report.original', { defaultValue: 'Original' }),
              },
              {
                value: 'final',
                label: t('vat-report.final-with-adjustments', {
                  defaultValue: 'Final (with adjustments)',
                }),
              },
            ]}
          />
        ) : null}
      </div>
    );
  };

  return (
    <VatReportWrapper
      className={pwaMobileNav ? 'scope-pwa-vat-report' : undefined}
      data-shell={pwaMobileNav ? 'pwa' : undefined}
    >
      {vatRunData && (
        <>
          {renderPwaSideMenuOverlay()}
          {pwaMobileNav ? (
            <div className="scope-pwa-vat-report-topbar">
              <button
                type="button"
                className="pwa-iconbtn subtle"
                aria-label={t('common.back')}
                onClick={handlePwaReturnBack}
              >
                <PwaIcon name="back" />
              </button>
              <div className="scope-pwa-vat-report-topbar__title">
                <strong>{pwaReturnTitle}</strong>
                {pwaReturnSubtitle ? <span>{pwaReturnSubtitle}</span> : null}
              </div>
              <button
                type="button"
                className={`pwa-iconbtn subtle${
                  showSideMenu ? ' is-open' : ''
                }`}
                aria-label={t('vat-report.return-workflow', {
                  defaultValue: 'Return workflow',
                })}
                aria-expanded={showSideMenu}
                onClick={() => setShowSideMenu((open) => !open)}
              >
                <PwaIcon name="layers" />
              </button>
            </div>
          ) : null}
          <div
            className={
              pwaMobileNav
                ? 'tabs-wrapper scope-pwa-vat-report-tabs'
                : 'tabs-wrapper'
            }
          >
            <Spin spinning={loading && !success} size="large">
              <ScopeTabs
                activeKey={activeKey}
                onChange={onTabChange}
                items={addTabItems(vatReportData)}
                size={pwaMobileNav ? 'small' : 'large'}
                tabBarGutter={pwaMobileNav ? 0 : 40}
                data-testid="tabs"
                className={
                  pwaMobileNav ? 'scope-pwa-vat-report-tabs__bar' : layoutClass
                }
                renderTabBar={
                  pwaMobileNav
                    ? (tabBarProps, DefaultTabBar) => (
                        <div className="scope-pwa-vat-report-tab-chrome">
                          <DefaultTabBar {...tabBarProps} />
                          {renderPwaActionsToolbar()}
                        </div>
                      )
                    : undefined
                }
              />
            </Spin>
          </div>
          {!pwaMobileNav ? (
            <div className={`top-wrapper ${tabsHeaderClass} vat-header`}>
              <div style={{ width: '100%' }} />
            </div>
          ) : null}
          {!pwaMobileNav ? renderReportTopToolbar() : null}

          {currentOrganisation?.organisationId && (
            <SseNotifications
              id={currentOrganisation.organisationId}
              setMessage={setSseMessage}
              type="report"
            />
          )}
        </>
      )}
    </VatReportWrapper>
  );
};
